import { Select, type SelectItem } from '@vrplatform/components';
import { useBilling, useTrpcMutation, useTeam } from '@vrplatform/data';
import { AlertCircleIcon, ChevronIcon } from '@vrplatform/icons';
import { LoadingIndicator } from '@vrplatform/components-legacy';
import type { ThemeColor } from '@vrplatform/theme';
import { showWarnNotification } from '@vrplatform/theme';
import { bankersRoundToCents } from '@vrplatform/utils';
import { Badge, Center, rem, useMantineTheme } from '@mantine/core';
import type { tax_statement_status_enum } from '@vrplatform/graphql';
import { memo } from 'react';
import { useAutomationHistoryDrawer } from '../../../../../hooks';
import {
  type TaxStatementRow,
  useTaxStatementCalculatedAmountQuery,
} from './useTaxStatementListTableQuery';
import {
  TaxStatementFiledIcon,
  TaxStatementInReviewIcon,
  useTaxStatementStatusOptions,
} from './useTaxStatementStatusOptions';

export type TaxStatementStatus = tax_statement_status_enum | 'error';

type Props = {
  statement: TaxStatementRow;
  openFilingConfirmModal: (statement: TaxStatementRow) => void;
  disabled?: boolean;
};

export function usePublishTaxStatementMutation() {
  const { loading, mutate } = useTrpcMutation('publishTaxStatement', {
    invalidateQueryKeys: ['taxStatements'],
    errorMessage: {
      title: 'Failed to update tax statement',
    },
    successMessage: {
      title: 'Tax statement updated',
      message: 'Tax statement status was updated.',
    },
  });

  const { open: openAutomationHistoryDrawer } = useAutomationHistoryDrawer();

  return {
    loading,
    mutate: (args: Parameters<typeof mutate>[0]) => {
      return mutate(args).then((result) => {
        if (result?.syncId)
          openAutomationHistoryDrawer(result.syncId, {
            refreshQueryKeys: ['taxStatements'],
          });
      });
    },
  };
}

const badgeColors: Record<TaxStatementStatus, ThemeColor> = {
  ignored: 'gray',
  draft: 'yellow',
  inReview: 'blue',
  ready: 'teal',
  submitted: 'green',
  error: 'red',
};

export const TaxStatementFiledBadge = () => (
  <Badge
    variant="light"
    color={badgeColors.submitted}
    size="md"
    sx={({ colors }) => ({
      textTransform: 'none',
      fontWeight: 500,
      paddingInline: rem(5),
      color: colors[badgeColors.submitted][9],
    })}
    leftSection={
      <Center>
        <TaxStatementFiledIcon />
      </Center>
    }
  >
    Filed
  </Badge>
);

export const TaxStatementReadyBadge = () => (
  <Badge
    variant="light"
    color={badgeColors.ready}
    size="md"
    sx={({ colors }) => ({
      textTransform: 'none',
      fontWeight: 500,
      paddingInline: rem(5),
      color: colors[badgeColors.ready][9],
    })}
    leftSection={
      <Center>
        <TaxStatementInReviewIcon color="#20C997" />
      </Center>
    }
  >
    Ready
  </Badge>
);

export const TaxStatementStatusSelect = memo(function TaxStatementStatusSelect({
  statement,
  openFilingConfirmModal,
  disabled: d,
}: Props) {
  const [{ id: teamId }] = useTeam();
  const { colors } = useMantineTheme();

  const { isReadOnly } = useBilling();
  const { data: calculatedAmountData, isLoading: loadingCalculatedAmount } =
    useTaxStatementCalculatedAmountQuery(statement);

  const currentStatus = statement.status;

  const { mutate, loading: loadingMutation } = usePublishTaxStatementMutation();

  const options = useTaxStatementStatusOptions(currentStatus, 'single');

  const calculatedAmount =
    statement.amount ?? calculatedAmountData?.calculatedAmount;
  const isCalculatedAmountLoading =
    typeof statement.amount === 'number' ? false : loadingCalculatedAmount;
  const disabled = d || isReadOnly || isCalculatedAmountLoading;

  const updateStatement = (newStatus: TaxStatementStatus) => {
    if (newStatus === currentStatus || isCalculatedAmountLoading) return;

    if (newStatus === 'submitted') {
      if (currentStatus === 'draft') {
        return showWarnNotification({
          message: 'Please review the statement before filing.',
        });
      }
      return openFilingConfirmModal(statement);
    }

    // just for typescript
    if (newStatus === 'error') return;

    if (typeof calculatedAmount !== 'number') {
      return showWarnNotification({
        message:
          'Please review the statement amount before updating the status.',
      });
    }

    mutate({
      teamId,
      status: newStatus,
      statements: [
        {
          year: statement.year,
          ownerId: statement.owner.id,
          centRentalRevenue: bankersRoundToCents(calculatedAmount),
          endAt: statement.endAt || undefined,
          startAt: statement.startAt || undefined,
        },
      ],
    });
  };

  if (currentStatus === 'error') {
    return (
      <Badge
        variant="light"
        color="red"
        size="md"
        sx={{
          textTransform: 'none',
          fontWeight: 500,
          paddingInline: rem(5),
          color: colors.red[9],
        }}
        leftSection={
          <Center>
            <AlertCircleIcon size={14} color={colors.red[5]} />
          </Center>
        }
      >
        {statement?.errorMessage || 'Missing info'}
      </Badge>
    );
  }

  if (currentStatus === 'submitted') {
    return <TaxStatementFiledBadge />;
  }

  const currentValue = options.find((o) => o.value === currentStatus) || null;

  return (
    <Select
      data={{
        options: options,
        sort: null,
      }}
      dropdownProps={{
        position: 'bottom-end',
        width: 340,
        withinPortal: true,
      }}
      type="single"
      inputProps={{
        loadingMutation,
        loadingQuery: isCalculatedAmountLoading,
        disabled,
      }}
      value={currentValue}
      setValue={(_, update) => {
        if (disabled) return;
        if (update.type === 'removed') return;

        updateStatement(update.value.value as TaxStatementStatus);
      }}
    >
      {({ disabled: dis, value, loadingMutation, loadingQuery }) => {
        const item = value as SelectItem | null;
        const status = item?.value as TaxStatementStatus;

        const disabled = dis || loadingMutation;

        return (
          <Badge
            variant="light"
            component="button"
            disabled={disabled}
            color={badgeColors[status]}
            size="md"
            sx={{
              cursor: disabled ? 'default' : 'pointer',
              paddingInline: rem(5),
              fontWeight: 500,
              textTransform: 'none',
              color: colors[badgeColors[status]][9],
            }}
            styles={{
              root: {
                backgroundColor: `${
                  colors[badgeColors[status]][0]
                }80!important`,
              },
            }}
            leftSection={
              <Center>
                {loadingMutation || loadingQuery ? (
                  <LoadingIndicator
                    size={'0.75rem'}
                    color={colors[badgeColors[status]][6]}
                  />
                ) : (
                  item?.icon
                )}
              </Center>
            }
            rightSection={
              !disabled && (
                <Center>
                  <ChevronIcon
                    size={14}
                    strokeWidth={1.5}
                    color={colors[badgeColors[status]][6]}
                  />
                </Center>
              )
            }
          >
            {(value as SelectItem)?.label}
          </Badge>
        );
      }}
    </Select>
  );
});
